import { cn } from '@solinkcorp/solink-ui';
import { useMemo } from 'react';
import { DEFAULT_SCROLL_TO_HOUR, HOURS_IN_DAY } from '../../constants';
import {
  computeDayPartOverlapLayout,
  getRangesForDay,
  hhmmToMinutes,
  type DayIndex,
  type ServiceDisplayDayPart,
} from '../../day-part/utils';
import { useScrollToHour } from '../../hooks/useScrollToHour';
import { DayPartCard } from './DayPartCard';
import { DayPartGridLines } from './DayPartGridLines';

interface DayPartScheduleViewContentProps {
  dayIndex: number;
  isWeekView: boolean;
  sidebarOpen: boolean;
  dayPartData: ServiceDisplayDayPart[];
  onEdit: (id: string) => void;
  onDeleteDayPart?: (id: string) => void;
}

export const DayPartScheduleViewContent = (props: DayPartScheduleViewContentProps) => {
  const { dayIndex, isWeekView, sidebarOpen, dayPartData, onEdit, onDeleteDayPart } = props;
  const width = isWeekView && !sidebarOpen ? 'w-[1px]' : isWeekView ? 'w-[74px]' : 'w-full';
  const { scrollTargetRef } = useScrollToHour(DEFAULT_SCROLL_TO_HOUR);

  const dayKey = String(dayIndex) as DayIndex;

  const onDelete = (id: string, _dayKey: DayIndex) => {
    onDeleteDayPart?.(id);
  };

  const dayPartCardsForDay = useMemo(
    () =>
      dayPartData.flatMap((dp) => {
        const ranges = getRangesForDay(dp.dayParts, dayKey);
        if (ranges.length === 0) return [];
        return [{ dp, range: ranges[0] }];
      }),
    [dayPartData, dayKey],
  );

  const overlapLayoutById = useMemo(
    () =>
      computeDayPartOverlapLayout(
        dayPartCardsForDay.map(({ dp, range }) => ({ id: dp.id, range })),
      ),
    [dayPartCardsForDay],
  );

  const reorderedDayPartCardsForDay = useMemo(() => {
    return [...dayPartCardsForDay].sort(
      (a, b) =>
        hhmmToMinutes(a.range.start) - hhmmToMinutes(b.range.start) ||
        hhmmToMinutes(b.range.end) - hhmmToMinutes(a.range.end) ||
        (overlapLayoutById.get(a.dp.id)?.columnIndex ?? 0) -
          (overlapLayoutById.get(b.dp.id)?.columnIndex ?? 0),
    );
  }, [dayPartCardsForDay, overlapLayoutById]);

  return (
    <div className={cn('relative grid', width)}>
      {/* Grid lines */}
      {sidebarOpen &&
        [...Array(HOURS_IN_DAY)].map((_, index) => (
          <DayPartGridLines
            key={index}
            index={index}
            isWeekView={isWeekView}
            scrollTargetRef={scrollTargetRef}
            targetScrollHour={DEFAULT_SCROLL_TO_HOUR}
          />
        ))}

      {/* Day part cards overlaid on the grid */}
      {sidebarOpen &&
        reorderedDayPartCardsForDay.map(({ dp, range }, rangeIdx) => (
          <DayPartCard
            key={`${range.start}-${dp.id}-${dayKey}-${rangeIdx}`}
            id={dp.id}
            label={dp.label}
            dayKey={dayKey}
            start={range.start}
            end={range.end}
            overlapLayout={
              overlapLayoutById.get(dp.id) ?? {
                columnIndex: 0,
                maxConcurrent: 1,
              }
            }
            onEdit={() => {
              onEdit(dp.id);
            }}
            onDelete={onDelete}
          />
        ))}
    </div>
  );
};
